import { ModalForm, ReactSelectStyled } from 'modules/app/styles'
import { useEffect, useRef, useState } from 'react'
import { Input, Modal, ModalBody } from 'reactstrap'
import { ReactComponent as CloseIcon } from 'assets/images/icons/close.svg'
import { ReactComponent as StripeLogo } from 'assets/images/logos/stripe_label.svg'
import { CardInput, CardType, PoweredByStripe } from './styles'
import { Col, FormGroup, Label, Row } from 'reactstrap'
import { formatCreditCardNumber, formatCVC, formatExpirationDate, handleApiError } from 'utils/helpers'
import Payment from 'payment'
import { CustomButton } from 'modules/auth/styles'
import { ButtonLoader } from 'utils/storageUtils'
import { addCard_action, toggleCardModal_action, getCompany_action } from 'store/actions'
import { connect } from 'react-redux'
import { Field, FormikProvider, useFormik } from 'formik'
import { getGICountryCodes_api, updateCompany_api } from 'store/apis'
import * as yup from 'yup'

const resetCountry = null

const resetCardFormData = {
  number: process.env.REACT_APP_NODE_ENV !== 'production' ? '4242 4242 4242 4242' : '',
  expiry: process.env.REACT_APP_NODE_ENV !== 'production' ? '12/24' : '',
  cvc: process.env.REACT_APP_NODE_ENV !== 'production' ? '123' : '',
  issuer: process.env.REACT_APP_NODE_ENV !== 'production' ? 'visa' : ''
}
function CardForm(props) {
  const { card } = props
  const [cardFormData, set_cardFormData] = useState(resetCardFormData)
  const [cardFormDataErrors, set_cardFormDataErrors] = useState({})
  const [countries, set_countries] = useState([])

  // AUTOFOCUS ON CC NUMBER FIELD
  let cc_numberRef = useRef(null)
  useEffect(() => {
    if (cc_numberRef && cc_numberRef.current) setTimeout(() => cc_numberRef.current.focus(), 200)
  }, [cc_numberRef])

  useEffect(() => {
    if (!card.id) {
      set_cardFormData(resetCardFormData)
    } else {
      set_cardFormData({
        id: card.id,
        number: `**** **** **** ${card.card.last4}`,
        expiry: `${card.card.exp_month}/${card.card.exp_year.toString().substr(-2)}`,
        cvc: '***',
        issuer: card.card.brand
      })
    }
    set_cardFormDataErrors({})
  }, [card])

  const handleInputChange = ({ target }) => {
    if (target.name === 'number') {
      set_cardFormDataErrors((values) => ({ ...values, number: false }))
      target.value = formatCreditCardNumber(target.value)
      set_cardFormData((values) => ({ ...values, issuer: Payment.fns.cardType(target.value) }))
    } else if (target.name === 'expiry') {
      set_cardFormDataErrors((values) => ({ ...values, expiry: false }))
      target.value = formatExpirationDate(target.value)
    } else if (target.name === 'cvc') {
      set_cardFormDataErrors((values) => ({ ...values, cvc: false }))
      target.value = formatCVC(target.value)
    }
    set_cardFormData((values) => ({ ...values, [target.name]: target.value }))
  }
  const validateChange = ({ target }) => {
    if (target.name === 'number') {
      if (!Payment.fns.validateCardNumber(target.value)) {
        set_cardFormDataErrors((values) => ({ ...values, number: true }))
      }
    } else if (target.name === 'expiry') {
      if (!Payment.fns.validateCardExpiry(target.value)) {
        set_cardFormDataErrors((values) => ({ ...values, expiry: true }))
      }
    } else if (target.name === 'cvc') {
      if (!Payment.fns.validateCardCVC(target.value)) {
        set_cardFormDataErrors((values) => ({ ...values, cvc: true }))
      }
    }
  }

  // Company Address Info
  const cardForm = useFormik({
    enableReinitialize: true,
    validateOnBlur: false,
    initialValues: {
      address: props.company.address,
      city: props.company.city,
      zip: props.company.zip,
      country: props.company.country ? countries[countries.map((c) => c.value).indexOf(props.company.country)] : null,
      vat: props.company.vat
    },
    validationSchema: yup.object().shape({
      country: yup.object().shape({
        value: yup.string().required(''),
        label: yup.string().required('')
      }),
      zip: yup
        .string()
        .nullable()
        .matches(/^[A-Za-z0-9]+$/, 'Invalid input')
        .max(12, 'Must be maximum 12 digits')
    }),
    onSubmit: async (values) => {
      // Submit country info
      if (!props.company.country) {
        try {
          const data = { ...props.company, ...values, country: values.country && values.country.value ? values.country.value : '' }
          await updateCompany_api(data, props.company.uuid)
          props.getCompany()
        } catch (error) {
          handleApiError(error)
        }
      }

      try {
        // Submit card info
        const data = {
          number: cardFormData.number.split(' ').join(''),
          exp_month: Payment.fns.cardExpiryVal(cardFormData.expiry).month,
          exp_year: Payment.fns.cardExpiryVal(cardFormData.expiry).year,
          cvc: cardFormData.cvc,
          plan: props.plan_selected.interval,
          showCreateItemPopup: props.showCreateItemPopup
        }
        await props.addCard(data)
        props.toggleCardModal(false)
        props.getCard && props.getCard()
      } catch (error) {
        handleApiError(error)
      }
    }
  })

  useEffect(() => {
    const getCountryCodes = async () => {
      try {
        const data = await getGICountryCodes_api()
        set_countries(data.map((c) => ({ value: c.id, label: c.name })))
      } catch (error) {
        handleApiError(error)
      }
    }
    getCountryCodes()
  }, [])

  return (
    <Modal isOpen={true} fade={false} centered toggle={() => props.toggleCardModal(false)}>
      <ModalBody>
        <ModalForm onSubmit={cardForm.handleSubmit}>
          <a
            href='/'
            className='modal-close'
            onClick={(e) => {
              e.preventDefault()
              props.toggleCardModal(false)
            }}>
            <CloseIcon />
          </a>
          <div className='d-flex'>
            <div className='title'>{cardFormData.id ? 'Manage' : 'New'} Credit Card</div>
            <PoweredByStripe className='web'>
              <StripeLogo />
            </PoweredByStripe>
          </div>
          <div className='mt-4'>
            <Row>
              <Col md={6}>
                <FormGroup style={{ marginBottom: 80 }}>
                  {cardFormData.issuer && <CardType className={cardFormData.issuer} />}
                  <CardInput
                    innerRef={cc_numberRef}
                    onChange={handleInputChange}
                    onBlur={validateChange}
                    type='tel'
                    name='number'
                    id='number'
                    disabled={cardFormData.id}
                    value={cardFormData.number}
                    pattern='[\d| ]{16,22}'
                    required
                    className={`${cardFormData.issuer ? 'pl-5' : ''}  ${cardFormData.number ? 'has-value' : ''} ${cardFormDataErrors.number ? 'error' : ''}`}
                  />
                  <Label htmlFor='number'>Card Number</Label>
                  {cardFormData.id && props.toggleRemoveCardModal && (
                    <a href='/' className='text-danger' onClick={(e) => props.toggleRemoveCardModal(true, e)} style={{ position: 'absolute', left: 0, bottom: -25 }}>
                      Remove
                    </a>
                  )}
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup style={{ marginBottom: 80 }}>
                  <CardInput
                    onChange={handleInputChange}
                    onBlur={validateChange}
                    type='tel'
                    name='expiry'
                    id='expiry'
                    disabled={cardFormData.id}
                    value={cardFormData.expiry}
                    pattern='\d\d/\d\d'
                    required
                    className={` ${cardFormData.expiry ? 'has-value' : ''} ${cardFormDataErrors.expiry ? 'error' : ''}`}
                  />
                  <Label htmlFor='expiry'>Expiry (mm/yy)</Label>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <CardInput
                    onChange={handleInputChange}
                    onBlur={validateChange}
                    type='tel'
                    id='cvc'
                    name='cvc'
                    disabled={cardFormData.id}
                    value={cardFormData.cvc}
                    pattern='\d{3,4}'
                    required
                    className={` ${cardFormData.cvc ? 'has-value' : ''} ${cardFormDataErrors.cvc ? 'error' : ''}`}
                  />
                  <Label htmlFor='cvc'>CVC/CVV</Label>
                </FormGroup>
                <PoweredByStripe className='mobile'>
                  <StripeLogo />
                </PoweredByStripe>
              </Col>
            </Row>
            {!props.company.country && (
              <FormikProvider value={cardForm}>
                <Row>
                  <Col xs={12} md={7}>
                    <Field name='address'>
                      {({ field }) => (
                        <FormGroup className='mb-5'>
                          <Input {...field} id='address' className={`${cardForm.errors.address ? 'error' : ''} ${cardForm.values.address ? 'has-value' : ''}`} />
                          <Label htmlFor='address'>Address <span className='optional-flag'>(optional)</span></Label>
                        </FormGroup>
                      )}
                    </Field>
                  </Col>
                  <Col xs={12} md={5}>
                    <Field name='city'>
                      {({ field }) => (
                        <FormGroup className='mb-5'>
                          <Input {...field} id='city' className={`${cardForm.errors.city ? 'error' : ''} ${cardForm.values.city ? 'has-value' : ''}`} />
                          <Label htmlFor='city'>City <span className='optional-flag'>(optional)</span></Label>
                        </FormGroup>
                      )}
                    </Field>
                  </Col>
                  <Col xs={12} md={3}>
                    <Field name='zip'>
                      {({ field }) => (
                        <FormGroup className='mb-5'>
                          <Input {...field} id='zip' type='text' className={`${cardForm.errors.zip ? 'error' : ''} ${cardForm.values.zip ? 'has-value' : ''}`} />
                          <Label htmlFor='zip'>Zip <span className='optional-flag'>(optional)</span></Label>
                        </FormGroup>
                      )}
                    </Field>
                  </Col>
                  <Col xs={12} md={4}>
                    <FormGroup className='mb-5'>
                      <ReactSelectStyled
                        id='country'
                        menuPlacement='top'
                        className={`select-control required-flag ${cardForm.errors.country ? 'error' : ''}`}
                        classNamePrefix='select-control'
                        isClearable
                        value={cardForm.values.country}
                        onChange={(obj) => cardForm.setFieldValue('country', obj || resetCountry)}
                        options={countries || []}
                        placeholder='Country'
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={12} md={5}>
                    <Field name='vat'>
                      {({ field }) => (
                        <FormGroup className='mb-5'>
                          <Input {...field} id='vat' type='number' className={`${cardForm.errors.vat ? 'error' : ''} ${cardForm.values.vat ? 'has-value' : ''}`} />
                          <Label htmlFor='vat'>VAT <span className='optional-flag'>(optional)</span></Label>
                        </FormGroup>
                      )}
                    </Field>
                  </Col>
                </Row>
              </FormikProvider>
            )}
            <div className='d-flex'>
              {!cardFormData.id && (
                <>
                  <CustomButton className='black mr-3 ' disabled={props.isSubmittingCard || cardFormDataErrors.number || cardFormDataErrors.expiry || cardFormDataErrors.cvc}>
                    {!props.is_subscribed ? `Subscribe` : `Save New Card`} {props.isSubmittingCard && <ButtonLoader />}
                  </CustomButton>
                  <CustomButton type='button' className='gray shadow-none' onClick={() => props.toggleCardModal(false)}>
                    Cancel
                  </CustomButton>
                </>
              )}
              {cardFormData.id && (
                <CustomButton type='button' className='gray shadow-none' onClick={() => set_cardFormData(resetCardFormData)}>
                  Replace card
                </CustomButton>
              )}
            </div>
          </div>
        </ModalForm>
      </ModalBody>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  company: state.company.company,
  isSubmittingCard: state.subscription.isSubmittingCard,
  isSubmittingSubscription: state.subscription.isSubmittingSubscription,
  is_subscribed: state.subscription.is_subscribed,
  subscription: state.subscription.subscription,
  showResumePlanModal: state.subscription.showResumePlanModal
})

const mapDispatchToProps = (dispatch) => ({
  addCard: (data) => new Promise((resolve, reject) => dispatch(addCard_action(data, resolve, reject))),
  toggleCardModal: (value) => dispatch(toggleCardModal_action(value)),
  getCompany: () => dispatch(getCompany_action())
})

export default connect(mapStateToProps, mapDispatchToProps)(CardForm)
